import { Col, Pagination } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent } from "vue";
import { setIsLoading as setIsLoadingApp } from "../app.logic";
import i18n from "../i18n";
import { createButtonCreate } from "./button";
import { has } from "./menu-context";

const CIcon = defineAsyncComponent(() => import("../components/icon/CIcon"));

export const createPage = ({ data, options }) => {
    return lodash.merge(
        {
            select: ["*"],
            join: [],
            where: [],
            sort: [],
            children: [],
            page: 0,
            size: 20,
            totalPage: 1,
            totalSize: data ? data.length : 0,
            isRecursive: false,
            isChildrenDataOnly: true,
            data: data ? data : []
        },
        options
    );
};

export const createOnChangePageList = ({ props, page, id, getId = ({ data }) => data.id, onClickView /* , onClickEdit */ }) => {
    const record = page.data.find(d => getId({ data: d }) === id);
    if (!record) {
        return null;
    }
    const paginationProps = {
        current: page.data.findIndex(d => getId({ data: d }) === id) + 1,
        pageSize: 1,
        total: page.data.length,
        "onUpdate:current": value => {
            const record = page.data.find((d, i) => i === value - 1);
            if (props.viewOnly) {
                onClickView({ record });
            } else {
                //onClickEdit({ record }) // comment because can edit must implement by role attribute or recrod status
                onClickView({ record });
            }
        },
        simple: true
    };
    return <Pagination {...paginationProps}></Pagination>;
};

export const createManageSelect = options => {
    return lodash.merge(
        {
            icon: () => <CIcon file="select.svg"></CIcon>,
            text: () => i18n.global.t("button.select"),
            force: true,
            visible: () => has({ menuContext: options?.menuContext, key: "canSelect", value: "true" }),
            rowSelection: true,
            buttonProp: { class: "btn-primary" },
            onClick: ({ rowSelection }) => window.onSelect?.({ rowSelection })
        },
        options
    );
};

export const createManageActive = options => {
    return lodash.merge(
        {
            icon: () => <CIcon file="active.svg"></CIcon>,
            text: () => i18n.global.t("button.active"),
            force: false,
            visible: () => has({ menuContext: options?.menuContext, key: "canActive" }),
            rowSelection: true,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.name).join(", ") }),
            buttonProp: { class: "btn-primary" },
            getCheckboxProps: ({ record }) => ({ disabled: record.isActive === true })
        },
        options
    );
};

export const createManageArchive = options => {
    return lodash.merge(
        {
            icon: () => <CIcon file="archive.svg"></CIcon>,
            text: () => i18n.global.t("button.archive"),
            force: false,
            visible: () => has({ menuContext: options?.menuContext, key: "canArchive" }),
            rowSelection: true,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.name).join(", "), okType: "danger" }),
            buttonProp: { class: "btn-danger" },
            getCheckboxProps: ({ record }) => ({ disabled: record.isActive === false })
        },
        options
    );
};

export const createManageDelete = options => {
    return lodash.merge(
        {
            icon: () => <CIcon file="delete.svg"></CIcon>,
            text: () => i18n.global.t("button.delete"),
            force: false,
            visible: () => has({ menuContext: options?.menuContext, key: "canDelete" }),
            rowSelection: true,
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.name).join(", "), okType: "danger" }),
            buttonProp: { class: "btn-danger" }
        },
        options
    );
};

export const createManageExportCsv = options => {
    return lodash.merge(
        {
            icon: () => <CIcon file="file-csv.svg"></CIcon>,
            text: () => i18n.global.t("button.exportCsv"),
            force: false,
            visible: () => true,
            buttonProp: { class: "btn-info" },
            onClick: ({ columns, page }) => {
                const { Parser } = require("json2csv");
                const { saveAs } = require("file-saver");
                const fields = columns
                    .filter(column => (column.dataIndex || column.dataIndexValue) && (column.visible?.() || column.visibleCsv?.()))
                    .map(column => ({
                        label: column.title?.(),
                        value: column.dataIndexValue ? row => column.dataIndexValue({ record: row, column }) : null || column.dataIndex
                    }));
                const parser = new Parser({ fields });
                if (options?.callData) {
                    options?.callData({ columns, page }).then(result => {
                        const csv = parser.parse(result.data);
                        var blob = new Blob([`\ufeff${csv}`], { type: "text/plain;charset=utf-8" });
                        saveAs(blob, "export.csv");
                    });
                } else {
                    const csv = parser.parse(page.data);
                    var blob = new Blob([`\ufeff${csv}`], { type: "text/plain;charset=utf-8" });
                    saveAs(blob, "export.csv");
                }
            }
        },
        options
    );
};

export const createManageExtraCreate = options => {
    return lodash.merge(
        {
            render: () => createButtonCreate({ onClick: options?.onClick }),
            visible: () => has({ menuContext: options?.menuContext, key: "canCreate" })
        },
        options
    );
};

export const createRowActionView = options => {
    return lodash.merge(
        {
            bind: { class: "btn-info", size: "small", icon: () => <CIcon file="eye.svg"></CIcon> },
            text: () => i18n.global.t("button.view"),
            showText: () => false,
            visible: () => has({ menuContext: options?.menuContext, key: "canView" })
        },
        options
    );
};

export const createRowActionEdit = options => {
    return lodash.merge(
        {
            bind: { class: "btn-warning", size: "small", icon: () => <CIcon file="edit.svg"></CIcon> },
            text: () => i18n.global.t("button.edit"),
            showText: () => false,
            visible: ({ record }) =>
                has({ menuContext: options?.menuContext, key: "canEdit" }) && ((record.id && record.isActive === true) || !record.id)
        },
        options
    );
};

export const createRowActionDelete = options => {
    return lodash.merge(
        {
            bind: { class: "btn-danger", size: "small", icon: () => <CIcon file="delete.svg"></CIcon> },
            text: () => i18n.global.t("button.delete"),
            showText: () => false,
            visible: ({ record }) =>
                has({ menuContext: options?.menuContext, key: "canDelete" }) && ((record.id && record.isActive === true) || !record.id)
        },
        options
    );
};

export const createRowActionPdf = options => {
    return lodash.merge(
        {
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="pdf.svg"></CIcon> },
            text: () => i18n.global.t("button.print"),
            showText: () => false,
            visible: () => true,
            onClick: ({ record }) =>
                onClickReport({
                    callReport: () => options.callReport?.({ record }),
                    fileName: options.fileName
                })
        },
        options
    );
};

export const createRowActionOpenUrl = options => {
    return lodash.merge(
        {
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="pdf.svg"></CIcon> },
            text: () => i18n.global.t("button.print"),
            showText: () => false,
            visible: () => true,
            onClick: ({ record }) => {
                const url = options.callUrl?.({ record });
                window.open(url);
            }
        },
        options
    );
};

export const createRowActionReStatus = options => {
    return lodash.merge(
        {
            bind: { class: "btn-danger", size: "small", icon: () => <CIcon file="re-status.svg"></CIcon> },
            text: () => i18n.global.t("button.reStatus"),
            showText: () => false,
            visible: () => true
        },
        options
    );
};

export const createAppPageHeaderTop = ({ props, data, state, routerPrefix, onClickView, onClickEdit }) => {
    let pageList = null;
    const { id } = data.useForm.modelRef;
    if (id && state.page.data.length) {
        pageList = createOnChangePageList({
            props,
            page: state.page,
            id,
            onClickView: ({ record }) => onClickView({ record, routerPrefix }),
            onClickEdit: ({ record }) => onClickEdit({ record, routerPrefix })
        });
    }
    return <Col class="ml-auto">{pageList}</Col>;
};

export const onClickReport = ({ callReport, fileName, type = "application/pdf" }) => {
    setIsLoadingApp(true);
    return callReport()
        .then(byte => {
            const blob = new Blob([byte], { type });
            if (type === "application/pdf") {
                var url = URL.createObjectURL(blob);
                window.open(url);
            } else {
                const { saveAs } = require("file-saver");
                saveAs(blob, fileName);
            }
        })
        .finally(() => setIsLoadingApp(false));
};
